/**
 * API list view
 * Displays all of the user's active
 * API instances. Can be fetched sorted but
 * current functionality to filter/search is
 * not active and is mocked.
 */

import { push } from "connected-react-router"
import React, { useMemo, useCallback } from "react"
import { connect } from "react-redux"
import { EmptyState } from "./Common/EmptyState"
import {
    loadMoreInstanceList,
    archiveApps,
    unArchiveApps
} from "../../../../../Actions/API/AppInstance/AppInstanceActions"
import {
    openProjectStatistics,
    openProject
} from "../../../../../Actions/API/Collaborator/CollaboratorActions"
import { useAppInstanceList } from "../../../../../Hooks/useAppInstanceList"
import {
    APIAppInstanceModel,
    AppInstanceListParams,
    AppInstanceOrderBy,
    AppInstanceStatus,
    getAppInstanceID,
    AppInstanceSortBy
} from "../../../../../Models/API/AppInstanceModel"
import { hasValue, getValue } from "../../../../../Models/Client/FetchState"
import { MainThunkDispatch } from "../../../../../Reducers"
import { routePaths } from "../../../../../Routes"
import { NavButton } from "../../../../Common/NavButton/NavButton"
import { OrgDashboardBase } from "../DashboardBase/OrgDashboardBase"
import { TableHeader } from "../../../../Common/Table/TableHeader"
import { appSortOptions } from "./Common/constants"
import { SortChooserProps } from "../../../../Common/Button/SortChooser"
import { SortExpression } from "../../../../../Models/Client/SortExpression"
import { ListView } from "../../../../Common/ListView/ListView"
import { useAppTemplateList } from "../../../../../Hooks/useAppTemplateList"
import { FlatButton } from "../../../../Common/FlatButton/FlatButton"
import { Menu, Loader } from "dash-styled"
import { useSelection } from "../../../../../Hooks/useSelection"
import { InstanceCardView } from "./Common/InstanceCardView"
import { useMyOrgRole } from "../../../../../Hooks/useMyOrgRole"
import { hasOrgPermission } from "../../../../../Models/Client/OrgPermissionScope"

interface AppListProps {
    orgUsername: string
    params: AppInstanceListParams
}

interface DispatchProps {
    navTo: (url: string) => void
    loadMore: () => Promise<void>
    openProject: (appID: string) => void
    openProjectStatistics: (appID: string) => void
    archiveApps: (appIDs: string[]) => Promise<void>
    unArchiveApps: (appIDs: string[]) => Promise<void>
}

export type Props = AppListProps & DispatchProps

export const AppListIsolated = ({
    orgUsername,
    navTo,
    loadMore,
    params,
    openProject: doOpenProject,
    openProjectStatistics: doOpenProjectStatistics,
    archiveApps: archiveAppsAction,
    unArchiveApps: unArchiveAppsAction
}: Props) => {
    const orgRoleFetch = useMyOrgRole(orgUsername)
    const appsFetch = useAppInstanceList(orgUsername, params)
    const templatesFetch = useAppTemplateList()
    const [selectedApps, toggleAppSelection, clearAppSelection] =
        useSelection<APIAppInstanceModel>(
            getAppInstanceID,
            hasValue(appsFetch) ? appsFetch.value.items : []
        )

    const handleChangeSortBy = useCallback(
        (value: SortExpression<APIAppInstanceModel>) => {
            navTo(
                routePaths.appList(orgUsername, {
                    ...params,
                    sortBy:
                        value === undefined
                            ? params.sortBy
                            : (value.sortBy as AppInstanceSortBy),
                    orderBy: value?.desc
                        ? AppInstanceOrderBy.desc
                        : AppInstanceOrderBy.asc
                })
            )
        },
        [navTo, orgUsername, params]
    )

    const isArchivedAppsVisible = useMemo(
        () => params.showArchived === true,
        [params]
    )
    const handleToggleArchivedAppsVisibility = useCallback(() => {
        navTo(
            routePaths.appList(orgUsername, {
                ...params,
                showArchived: !isArchivedAppsVisible
            })
        )
    }, [navTo, orgUsername, params, isArchivedAppsVisible])

    const sortOptions: SortChooserProps<APIAppInstanceModel> = useMemo(
        () => ({
            fields: appSortOptions,
            applied: {
                sortBy: params.sortBy,
                desc: params.orderBy === AppInstanceOrderBy.desc
            },
            onChange: handleChangeSortBy
        }),
        [handleChangeSortBy, params]
    )

    const selectedAppsIDs = useMemo(
        () => selectedApps.map(getAppInstanceID),
        [selectedApps]
    )

    const handleArchivingApps = useCallback(async () => {
        await archiveAppsAction(selectedAppsIDs)
        clearAppSelection()
    }, [selectedAppsIDs, archiveAppsAction, clearAppSelection])

    const handleUnarchivingApps = useCallback(async () => {
        await unArchiveAppsAction(selectedAppsIDs)
        clearAppSelection()
    }, [selectedAppsIDs, unArchiveAppsAction, clearAppSelection])

    const extraActions = useMemo(() => {
        const apps = getValue(appsFetch)
        const canArchiveApps = !!selectedApps.find(
            app => app.status === AppInstanceStatus.ENABLED
        )
        const canUnarchiveApps = !!selectedApps.find(
            app => app.status === AppInstanceStatus.DISABLED
        )

        const actions = ([] as [string, () => void][]).concat(
            canArchiveApps ? [["Archive selected", handleArchivingApps]] : [],
            canUnarchiveApps
                ? [["Unarchive selected", handleUnarchivingApps]]
                : []
        )

        const handleExtraAction = (label: string) => {
            const action = actions.find(a => a[0] === label)

            if (action !== undefined) {
                action[1]?.()
            }
        }

        return (
            <>
                {apps !== undefined && apps.totalArchived > 0 && (
                    <FlatButton
                        onClick={handleToggleArchivedAppsVisibility}
                        className="ml-2"
                        testId={
                            isArchivedAppsVisible
                                ? "hide-archived-button"
                                : "show-archived-button"
                        }
                    >
                        {(isArchivedAppsVisible ? "Hide" : "Show") +
                            ` ${apps?.totalArchived ?? 0} archived`}
                    </FlatButton>
                )}
                <Menu
                    title="Actions"
                    align="right"
                    className="ml-2"
                    disabled={actions.length === 0}
                    items={actions.map(([label]) => label)}
                    onChoose={handleExtraAction}
                />
            </>
        )
    }, [
        appsFetch,
        selectedApps,
        handleArchivingApps,
        handleUnarchivingApps,
        handleToggleArchivedAppsVisibility,
        isArchivedAppsVisible
    ])
    const renderContentBasedOnValue = () => {
        if (
            !hasValue(appsFetch) ||
            !hasValue(templatesFetch) ||
            !hasValue(orgRoleFetch)
        ) {
            return <Loader testId="projects-loader" />
        }

        if (appsFetch.value.total === 0) {
            return (
                <EmptyState
                    canCreateApp={hasOrgPermission(
                        orgRoleFetch.value,
                        "project:create"
                    )}
                />
            )
        }

        return (
            <>
                {hasOrgPermission(orgRoleFetch.value, "project:create") && (
                    <NavButton
                        to={routePaths.store()}
                        variant="contained"
                        color="darkviolet"
                        className="w-48 flex-shrink-0"
                    >
                        New project
                    </NavButton>
                )}
                <TableHeader
                    className="flex-shrink-0"
                    totalCount={
                        appsFetch.value.total +
                        (isArchivedAppsVisible
                            ? 0
                            : appsFetch.value.totalArchived)
                    }
                    sortOptions={sortOptions}
                    filteredCount={
                        isArchivedAppsVisible
                            ? undefined
                            : appsFetch.value.total
                    }
                    extraActions={extraActions}
                />
                <ListView
                    data={appsFetch.value.items}
                    item={itemView}
                    loadMore={loadMore}
                    totalCount={appsFetch.value.total}
                />
            </>
        )
    }

    /**
     * this will require doing a fetch for a job list
     * filtered by completed, max length 1, page 0, sort by completed, order by desc
     * then, get response
     *
     * most recently updated job (text)
     * app name (tittle)
     * status (pill)
     * data render (input output render)
     *   data is most recent completed, or template example, or data type
     *  - input (data render)
     *  - output (data render)
     * total jobs (text)
     * % completed (text + progress bar)
     */
    const itemView = (instance: APIAppInstanceModel) => (
        <InstanceCardView
            key={instance.uuid}
            instance={instance}
            isChecked={selectedAppsIDs.includes(instance.uuid)}
            doOpenProjectStatistics={doOpenProjectStatistics}
            doOpenProject={doOpenProject}
            onToggleCheck={toggleAppSelection}
        />
    )

    return (
        <OrgDashboardBase
            orgUsername={orgUsername}
            title="Projects"
            titleComponent="h1"
            fixedContentWidth={false}
        >
            {renderContentBasedOnValue()}
        </OrgDashboardBase>
    )
}

const mapDispatchToProps = (
    dispatch: MainThunkDispatch,
    { orgUsername }: AppListProps
): DispatchProps => {
    return {
        navTo: (url: string) => dispatch(push(url)),
        loadMore: () => dispatch(loadMoreInstanceList(orgUsername)),
        openProject: (appID: string) =>
            dispatch(openProject(orgUsername, appID)),
        openProjectStatistics: (appID: string) =>
            dispatch(openProjectStatistics(orgUsername, appID)),
        archiveApps: async (appIDs: string[]) =>
            dispatch(archiveApps(orgUsername, appIDs)),
        unArchiveApps: async (appIDs: string[]) =>
            dispatch(unArchiveApps(orgUsername, appIDs))
    }
}

export const AppList = connect(null, mapDispatchToProps)(AppListIsolated)
